import { useEffect, useMemo } from "react";
import type { QueryBuilder } from "@cgarciagarcia/react-query-builder";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { addMonths, format, isAfter, startOfMonth } from "date-fns";

import { parseSlotDate, SLOT_DATE_FORMAT } from "~/shared/utils/slot-date";
import { errorToast, toast } from "~/ui";
import {
  downloadScheduleChangeLog,
  exportProviderSchedule,
  getProviderSlots,
} from "./api";
import { mutations, queries } from "./factories";
import type {
  ConvertProviderSlotParams,
  ProviderSlotFilters,
  ProviderSlotRow,
  UploadProviderScheduleParams,
} from "./types";

const SCHEDULE_META_STALE_TIME_MS = 60 * 1000;

const nextMonthCursor = (startDate: string) =>
  format(
    startOfMonth(addMonths(parseSlotDate(startDate), 1)),
    SLOT_DATE_FORMAT,
  );

export const useAdminProvidersQuery = ({
  queryBuilder,
}: {
  queryBuilder?: QueryBuilder<Record<string, string> | undefined>;
}) => {
  return useQuery(queries.list({ queryBuilder }));
};

type UseUploadProviderScheduleOptions = {
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
};

export const useUploadProviderScheduleMutation = (
  options?: UseUploadProviderScheduleOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UploadProviderScheduleParams) =>
      mutations.uploadProviderSchedule(params),
    onSuccess: (_, params) => {
      const filename = params.file?.name;

      toast({
        type: "success",
        title: `${filename ?? "File"} was saved and is now the active schedule template.`,
      });
      queryClient.invalidateQueries({
        queryKey: queries.scheduleMeta(params.providerId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: ["adminProviders"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queries.orphanedAppointments(params.providerId).queryKey,
      });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useGetProviderScheduleMeta = (providerId: string) => {
  return useQuery({
    ...queries.scheduleMeta(providerId),
    enabled: !!providerId,
    staleTime: SCHEDULE_META_STALE_TIME_MS,
  });
};

export const useExportProviderSchedule = () => (providerId: string) =>
  exportProviderSchedule(providerId);

export const useDownloadScheduleChangeLog = () => (providerId: string) =>
  downloadScheduleChangeLog(providerId);

export const useDeleteProviderScheduleMutation = (
  providerId: string,
  options?: { onCloseModal?: () => void },
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (filename?: string) => {
      await mutations.deleteProviderSchedule(providerId);
      return filename;
    },
    onSuccess: (filename) => {
      queryClient.invalidateQueries({
        queryKey: queries.scheduleMeta(providerId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: ["adminProviders"],
        exact: false,
      });

      toast({
        type: "success",
        title: "Schedule deleted",
        description: `${filename ?? "Schedule"} was deleted and is no longer active.`,
      });

      options?.onCloseModal?.();
    },
    onError: (error) => {
      errorToast(error);
      options?.onCloseModal?.();
    },
  });
};

export const useGetProviderSlots = ({
  providerId,
  startDate,
}: {
  providerId: string;
  startDate: string;
}) => {
  return useQuery({
    ...queries.slots({ providerId, startDate }),
    enabled: !!providerId && !!startDate,
  });
};

export const useGetProviderSlotsInfinite = ({
  providerId,
  enabled = true,
  visitTypes,
  statuses,
}: {
  providerId: string;
  enabled?: boolean;
} & ProviderSlotFilters) => {
  const { data, isPending, isError, hasNextPage, isFetching, fetchNextPage } =
    useInfiniteQuery({
      queryKey: queries.slotsInfinite({ providerId, visitTypes, statuses })
        .queryKey,
      queryFn: ({ pageParam }) =>
        getProviderSlots({
          providerId,
          startDate: pageParam,
          visitTypes,
          statuses,
        }),
      initialPageParam: format(new Date(), SLOT_DATE_FORMAT),
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const cursor = nextMonthCursor(lastPageParam);

        return isAfter(
          parseSlotDate(cursor),
          parseSlotDate(lastPage.meta.maxDate),
        )
          ? undefined
          : cursor;
      },
      enabled: !!providerId && enabled,
    });

  const loadedPageCount = data?.pages.length ?? 0;
  const lastLoadedPage = data?.pages.at(-1);
  const lastPageIsEmpty =
    lastLoadedPage !== undefined &&
    Object.keys(lastLoadedPage.data).length === 0;

  useEffect(() => {
    if (!enabled || !lastPageIsEmpty || !hasNextPage || isFetching) {
      return;
    }

    void fetchNextPage({ cancelRefetch: false });
  }, [
    enabled,
    lastPageIsEmpty,
    hasNextPage,
    isFetching,
    fetchNextPage,
    loadedPageCount,
  ]);

  const rows = useMemo<ProviderSlotRow[]>(
    () =>
      (data?.pages ?? []).flatMap((page) =>
        Object.entries(page.data).flatMap(([date, slots]) =>
          slots.map((slot) => ({ ...slot, date })),
        ),
      ),
    [data],
  );

  return {
    rows,
    isPending,
    isError,
    hasNextPage,
    isFetching,
    fetchNextPage,
  };
};

export const useConvertProviderSlotMutation = (
  { providerId }: { providerId: string },
  options?: { onSuccess?: () => void },
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Omit<ConvertProviderSlotParams, "providerId">) =>
      mutations.convertProviderSlot({ ...params, providerId }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queries.slots._def }),
        queryClient.invalidateQueries({ queryKey: queries.slotsInfinite._def }),
      ]);
      options?.onSuccess?.();
    },
  });
};

export const useGetProviderOrphanedAppointments = (
  providerId: string,
  enabled = true,
) => {
  return useQuery({
    ...queries.orphanedAppointments(providerId),
    enabled: !!providerId && enabled,
    refetchOnWindowFocus: false,
  });
};
